/**
 * Chat Store - Zustand
 * 
 * Manages:
 * - Chat threads (conversations)
 * - Messages per thread
 * - Typing indicators
 * - Read receipts
 * - Message persistence
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { logger } from '@/lib/logger';

export interface ChatMessage {
  id: string;
  threadId: string;
  senderId: string;
  senderName: string;
  senderRole: 'rider' | 'customer' | 'vendor' | 'ops';
  message: string;
  timestamp: string;
  deliveredAt?: string;
  readAt?: string;
  localOnly?: boolean; // For offline messages
}

export interface ChatThread {
  id: string;
  orderId: string;
  participants: {
    customerId: string;
    customerName: string;
    vendorId?: string;
    vendorName?: string;
    riderId: string;
    riderName: string;
  };
  lastMessage?: ChatMessage;
  unreadCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface TypingIndicator {
  threadId: string;
  userId: string;
  userName: string;
  timestamp: number;
}

interface ChatState {
  // Threads
  threads: ChatThread[];
  activeThreadId: string | null;
  
  // Messages (keyed by threadId)
  messagesByThread: Record<string, ChatMessage[]>;
  
  // Typing indicators
  typingIndicators: TypingIndicator[];
  
  // Loading states
  isLoadingThreads: boolean;
  isLoadingMessages: boolean;
  isSendingMessage: boolean;
  
  // Actions - Threads
  setThreads: (threads: ChatThread[]) => void;
  addThread: (thread: ChatThread) => void;
  setActiveThread: (threadId: string | null) => void;
  updateThreadLastMessage: (threadId: string, message: ChatMessage) => void;
  incrementUnreadCount: (threadId: string) => void;
  markThreadAsRead: (threadId: string) => void;
  
  // Actions - Messages
  setMessages: (threadId: string, messages: ChatMessage[]) => void;
  addMessage: (message: ChatMessage) => void;
  updateMessageStatus: (messageId: string, status: { deliveredAt?: string; readAt?: string }) => void;
  
  // Actions - Typing
  setTypingIndicator: (indicator: TypingIndicator) => void;
  removeTypingIndicator: (threadId: string, userId: string) => void;
  clearOldTypingIndicators: () => void;
  
  // Actions - Loading
  setIsLoadingThreads: (loading: boolean) => void;
  setIsLoadingMessages: (loading: boolean) => void;
  setIsSendingMessage: (sending: boolean) => void;
  
  // Reset
  clearChat: () => void;
}

const TYPING_INDICATOR_TIMEOUT = 3000; // 3 seconds

export const useChatStore = create<ChatState>()(
  persist(
    (set, get) => ({
      threads: [],
      activeThreadId: null,
      messagesByThread: {},
      typingIndicators: [],
      isLoadingThreads: false,
      isLoadingMessages: false,
      isSendingMessage: false,

      setThreads: (threads) => {
        logger.info('Chat threads updated', { count: threads.length });
        set({ threads });
      },

      addThread: (thread) => {
        const { threads } = get();
        
        // Prevent duplicates
        if (threads.some(t => t.id === thread.id)) {
          logger.warn('Thread already exists', { threadId: thread.id });
          return;
        }

        logger.info('Thread added', { threadId: thread.id });
        set({ threads: [thread, ...threads] });
      },

      setActiveThread: (threadId) => {
        logger.info('Active thread changed', { threadId });
        set({ activeThreadId: threadId });
        
        // Mark as read when opened
        if (threadId) {
          get().markThreadAsRead(threadId);
        }
      },

      updateThreadLastMessage: (threadId, message) => {
        const { threads } = get();
        const updatedThreads = threads.map(thread => 
          thread.id === threadId
            ? { ...thread, lastMessage: message, updatedAt: message.timestamp }
            : thread
        );
        
        // Sort threads by last message timestamp
        updatedThreads.sort((a, b) => 
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        );
        
        set({ threads: updatedThreads });
      },

      incrementUnreadCount: (threadId) => {
        const { threads, activeThreadId } = get();
        
        // Don't increment if thread is currently active
        if (activeThreadId === threadId) {
          return;
        }
        
        const updatedThreads = threads.map(thread =>
          thread.id === threadId
            ? { ...thread, unreadCount: thread.unreadCount + 1 }
            : thread
        );
        
        set({ threads: updatedThreads });
      },

      markThreadAsRead: (threadId) => {
        const { threads } = get();
        const updatedThreads = threads.map(thread =>
          thread.id === threadId
            ? { ...thread, unreadCount: 0 }
            : thread
        );
        
        set({ threads: updatedThreads });
      },

      setMessages: (threadId, messages) => {
        const { messagesByThread } = get();
        set({
          messagesByThread: {
            ...messagesByThread,
            [threadId]: messages,
          },
        });
      },

      addMessage: (message) => {
        const { messagesByThread } = get();
        const threadMessages = messagesByThread[message.threadId] || [];
        
        // Prevent duplicates (exact-once delivery guarantee)
        if (threadMessages.some(m => m.id === message.id)) {
          logger.warn('Duplicate message prevented', { messageId: message.id });
          return;
        }

        logger.info('Message added', { 
          messageId: message.id,
          threadId: message.threadId,
        });

        set({
          messagesByThread: {
            ...messagesByThread,
            [message.threadId]: [...threadMessages, message],
          },
        });

        // Update thread's last message
        get().updateThreadLastMessage(message.threadId, message);

        // Increment unread count if not sender
        if (message.senderRole !== 'rider') {
          get().incrementUnreadCount(message.threadId);
        }
      },

      updateMessageStatus: (messageId, status) => {
        const { messagesByThread } = get();
        const updatedMessages = { ...messagesByThread };
        
        for (const threadId in updatedMessages) {
          updatedMessages[threadId] = updatedMessages[threadId].map(msg =>
            msg.id === messageId
              ? { ...msg, ...status }
              : msg
          );
        }

        set({ messagesByThread: updatedMessages });
      },

      setTypingIndicator: (indicator) => {
        const { typingIndicators } = get();
        
        // Remove old indicator from same user in same thread
        const filtered = typingIndicators.filter(
          t => !(t.threadId === indicator.threadId && t.userId === indicator.userId)
        );

        set({ typingIndicators: [...filtered, indicator] });
      },

      removeTypingIndicator: (threadId, userId) => {
        const { typingIndicators } = get();
        set({
          typingIndicators: typingIndicators.filter(
            t => !(t.threadId === threadId && t.userId === userId)
          ),
        });
      },

      clearOldTypingIndicators: () => {
        const { typingIndicators } = get();
        const now = Date.now();
        
        set({
          typingIndicators: typingIndicators.filter(
            t => now - t.timestamp < TYPING_INDICATOR_TIMEOUT
          ),
        });
      },

      setIsLoadingThreads: (loading) => set({ isLoadingThreads: loading }),
      setIsLoadingMessages: (loading) => set({ isLoadingMessages: loading }),
      setIsSendingMessage: (sending) => set({ isSendingMessage: sending }),

      clearChat: () => {
        logger.info('Chat cleared');
        set({
          threads: [],
          activeThreadId: null,
          messagesByThread: {},
          typingIndicators: [],
        });
      },
    }),
    {
      name: 'rider-chat-storage',
      partialize: (state) => ({
        threads: state.threads,
        messagesByThread: state.messagesByThread,
      }),
    }
  )
);


















